import type { DragLocationHistory } from '@atlaskit/pragmatic-drag-and-drop/types';
import { KNOWN_COLUMN_TYPES } from '@atlassian/jira-issue-table-common/src/constants.tsx';
import {
	COLUMN_TYPE_DATE_PICKER,
	COLUMN_TYPE_SATISFACTION_FEEDBACK,
	COLUMN_TYPE_SENTIMENT,
	MAXIMUM_COLUMN_WIDTH,
	MINIMUM_COLUMN_WIDTH,
	MINIMUM_COLUMN_WIDTH_COMPACT,
	MINIMUM_CONFLUENCE_COLUMN_WIDTH,
	MINIMUM_DATEPICKER_COLUMN_WIDTH,
	MINIMUM_DATETIME_COLUMN_WIDTH,
	MINIMUM_GOALS_COLUMN_WIDTH,
	MINIMUM_MULTI_USER_SELECT_COLUMN_WIDTH,
	MINIMUM_SATISFACTION_COLUMN_WIDTH,
	MINIMUM_SENTIMENT_COLUMN_WIDTH,
} from '../../../../common/constants.tsx';

const CSS_RESIZING_WIDTH_PROPERTY = '--local-resizing-width';

export const setCssResizingWidth = (container: HTMLElement | null, width: number) => {
	container?.style.setProperty(CSS_RESIZING_WIDTH_PROPERTY, `${width}px`);
};
export const clearCssResizingWidth = (container: HTMLElement | null) => {
	container?.style.removeProperty(CSS_RESIZING_WIDTH_PROPERTY);
};

export const disableCssPointerEvents = (container: HTMLElement | null | undefined) => {
	container?.style.setProperty('pointer-events', 'none');
};
export const enableCssPointerEvents = (container: HTMLElement | null | undefined) => {
	container?.style.removeProperty('pointer-events');
};

export const ensureWidthWithinLimits = (width: number, isCompactColumnEnabled?: boolean) => {
	return Math.min(
		Math.max(isCompactColumnEnabled ? MINIMUM_COLUMN_WIDTH_COMPACT : MINIMUM_COLUMN_WIDTH, width),
		MAXIMUM_COLUMN_WIDTH,
	);
};

export const getProposedWidth = ({
	initialWidth,
	isCompactColumnEnabled,
	location,
	isSingleRowHeightEnabled,
	columnType,
}: {
	initialWidth: number;
	isCompactColumnEnabled?: boolean;
	location: DragLocationHistory;
	isSingleRowHeightEnabled?: boolean;
	columnType?: string;
}): number => {
	const diffX = location.current.input.clientX - location.initial.input.clientX;
	const proposedWidth = initialWidth + diffX;

	// ensure we don't go below the min allowed width
	const width = ensureWidthWithinLimits(proposedWidth, isCompactColumnEnabled);

	if (!isSingleRowHeightEnabled) {
		return width;
	}

	return getMinimumColumnProposedWidth({
		columnType,
		width,
	});
};

const minimumColumnWidthMapping = new Map([
	[KNOWN_COLUMN_TYPES.DATE_TIME, MINIMUM_DATETIME_COLUMN_WIDTH],
	[KNOWN_COLUMN_TYPES.CREATED, MINIMUM_DATETIME_COLUMN_WIDTH],
	[KNOWN_COLUMN_TYPES.UPDATED, MINIMUM_DATETIME_COLUMN_WIDTH],
	[KNOWN_COLUMN_TYPES.MULTI_USER_PICKER, MINIMUM_MULTI_USER_SELECT_COLUMN_WIDTH],
	[KNOWN_COLUMN_TYPES.DUE_DATE, MINIMUM_DATEPICKER_COLUMN_WIDTH],
	[KNOWN_COLUMN_TYPES.GOALS, MINIMUM_GOALS_COLUMN_WIDTH],
	[KNOWN_COLUMN_TYPES.CONFLUENCE_PAGES, MINIMUM_CONFLUENCE_COLUMN_WIDTH],
	[COLUMN_TYPE_DATE_PICKER, MINIMUM_MULTI_USER_SELECT_COLUMN_WIDTH],
	[COLUMN_TYPE_SATISFACTION_FEEDBACK, MINIMUM_SATISFACTION_COLUMN_WIDTH],
	[COLUMN_TYPE_SENTIMENT, MINIMUM_SENTIMENT_COLUMN_WIDTH],
]);

// Get the minimum width of the column
export const getMinimumColumnWidth = (columnType: string) =>
	minimumColumnWidthMapping.get(columnType) || MINIMUM_COLUMN_WIDTH_COMPACT;

// Get the minimum width of the column given a proposed width
export const getMinimumColumnProposedWidth = ({
	columnType,
	width,
}: {
	columnType?: string;
	width: number;
}) => {
	return columnType ? Math.max(width, getMinimumColumnWidth(columnType)) : width;
};
